import { useEffect, useRef } from "react";

/**
 * Custom hook to handle auto-scrolling that stops on user interaction.
 * @param {boolean} trigger - When true, start auto-scrolling.
 * @param {number} speed - The speed of scrolling (interval in ms). Default is 30ms.
 * @param {number} step - Pixels to scroll each step. Default is 1.
 */
const useAutoScroll = (trigger, speed = 30, step = 1, delay = 3000) => {
    const scrollIntervalRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        const stopAutoScroll = () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;

                // Remove listeners once stopped
                window.removeEventListener("wheel", stopAutoScroll);
                window.removeEventListener("touchstart", stopAutoScroll);
                window.removeEventListener("mousedown", stopAutoScroll);
                window.removeEventListener("keydown", stopAutoScroll);
            }
        };

        const startAutoScroll = () => {
            if (scrollIntervalRef.current || scrollTimeoutRef.current) return;

            // Wait for the specified delay before starting the auto-scroll
            scrollTimeoutRef.current = setTimeout(() => {
                scrollIntervalRef.current = setInterval(() => {
                    window.scrollBy({
                        top: step,
                        behavior: "auto",
                    });

                    // Stop if we reached the bottom
                    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
                        stopAutoScroll();
                    }
                }, speed);
            }, delay);
        };

        if (trigger) {
            startAutoScroll();

            // Add interaction listeners to stop
            window.addEventListener("wheel", stopAutoScroll);
            window.addEventListener("touchstart", stopAutoScroll);
            window.addEventListener("mousedown", stopAutoScroll);
            window.addEventListener("keydown", stopAutoScroll);
        }

        return () => {
            stopAutoScroll();
            // Cleanup listeners also if trigger changes back to false
            window.removeEventListener("wheel", stopAutoScroll);
            window.removeEventListener("touchstart", stopAutoScroll);
            window.removeEventListener("mousedown", stopAutoScroll);
            window.removeEventListener("keydown", stopAutoScroll);
        };
    }, [trigger, speed, step, delay]);
};

export default useAutoScroll;
